<script setup>
import { router } from '@inertiajs/vue3';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, externalService, websiteVariants } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    externalService: {
        required: true,
        type: Object,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

console.log(externalService);

const form = reactive({
    active: externalService.active,
    siteUrl: externalService.data.siteUrl,
    websiteVariants: websiteVariants.reduce((acc, websiteVariant) => {
        acc[websiteVariant.id] = {
            siteUrl:
                externalService.data.websiteVariants &&
                externalService.data.websiteVariants[websiteVariant.id]
                    ? externalService.data.websiteVariants[websiteVariant.id]
                          .siteUrl
                    : '',
            useDefaultValue:
                externalService.data.websiteVariants &&
                externalService.data.websiteVariants[websiteVariant.id]
                    ? externalService.data.websiteVariants[websiteVariant.id]
                          .useDefaultValue
                    : true,
        };

        return acc;
    }, {}),
});

const validationSchema = object({
    active: boolean().required('Ce champ est requis.'),
    siteUrl: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postGoogleSearchConsole',
                {
                    tenant: tenant.id,
                },
            ),
            {
                active: form.active,
                siteUrl: form.siteUrl,
                websiteVariants: form.websiteVariants,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.customer.externalServices.googleSearchConsole',
                    {
                        tenant: tenant.id,
                    },
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.customer.externalServices'),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.active"
                    label="Actif"
                    field-name="active"
                    field-id="active"
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.siteUrl"
                        label="URL du site"
                        field-name="siteUrl"
                        field-id="siteUrl"
                    />
                </FormGrid>
            </FormContent>

            <FormContent
                v-for="websiteVariant in websiteVariants"
                :key="websiteVariant.id"
            >
                <FormTitle>
                    {{ websiteVariant.name }}
                </FormTitle>

                <FormGrid :columns="2">
                    <InputField
                        v-model="
                            form.websiteVariants[websiteVariant.id].siteUrl
                        "
                        label="URL du site"
                        :field-name="
                            'websiteVariants[' + websiteVariant.id + '].siteUrl'
                        "
                        :field-id="
                            'websiteVariants[' + websiteVariant.id + '].siteUrl'
                        "
                        :disabled="
                            form.websiteVariants[websiteVariant.id]
                                .useDefaultValue
                        "
                    />
                </FormGrid>

                <SwitchField
                    v-model="
                        form.websiteVariants[websiteVariant.id].useDefaultValue
                    "
                    label="Utiliser la valeur par défaut"
                    :field-id="
                        'websiteVariants[' +
                        websiteVariant.id +
                        '].useDefaultValue.'
                    "
                    :field-name="
                        'websiteVariants[' +
                        websiteVariant.id +
                        '].useDefaultValue'
                    "
                    inline
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
